import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { ensureListing, ensureTransaction } from '../../util/data';
import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  NamedRedirect,
  ApplicationPanel,
} from '../../components';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import { TopbarContainer } from '../../containers';
import {
  createReverseTransaction,
  loadData,
  setInitialValues,
  showProfileListing,
  acceptApplication,
  skipApplication,
  acceptAfterSkip,
} from './ApplicationPage.duck.js';
import config from '../../config';
import {
  uploadAttachment,
  removeAttachment,
  clearStagedAttachments,
  stagedAttachmentsList,
  readyAttachmentFileIds,
  SCOPE_APPLICATION_PANEL,
} from '../../ducks/messageAttachments.duck';
import css from './ApplicationPage.css';

const PROVIDER = 'provider';
const CUSTOMER = 'customer';

export const ApplicationPageComponent = props => {
  const {
    intl,
    isScrollingDisabled,
    transaction,
    currentUser,
    transactionRole,
    params,
    fetchTransactionError,
    onAcceptApplication,
    onCreateReverseTransaction,
    onShowProfileListing,
    onSkipApplication,
    onAcceptAfterSkip,
    onManageDisableScrolling,
    stagedAttachments,
    onUploadAttachment,
    onRemoveAttachment,
    onClearAttachments,
    sendAcceptApplicationInProgress,
    showProfileListingInProgress,
    sendCreateReverseTransactionInProgress,
    sendSkipApplicationInProgress,
    sendAcceptAfterSkipInProgress,
    sendAcceptApplicationError,
    showProfileListingError,
    sendCreateReverseTransactionError,
    sendSkipApplicationError,
    sendAcceptAfterSkipError,
    portfolioWorks,
    fetchPortfolioWorksInProgress,
    skillOptions,
    toolOptions,
    history,
  } = props;

  const currentTransaction = ensureTransaction(transaction);
  const currentListing = ensureListing(currentTransaction.listing);
  const isProviderRole = transactionRole === PROVIDER;
  const isCustomerRole = transactionRole === CUSTOMER;

  // Redirect users with someone else's direct link to their own inbox/sales or inbox/orders page.
  const isDataAvailable =
    currentUser &&
    currentTransaction.id &&
    currentTransaction.id.uuid === params.id &&
    currentTransaction.attributes.lineItems &&
    currentTransaction.customer &&
    currentTransaction.provider &&
    !fetchTransactionError;

  const isOwnSale =
    isDataAvailable &&
    isProviderRole &&
    currentUser.id.uuid === currentTransaction.provider.id.uuid;
  const isOwnOrder =
    isDataAvailable &&
    isCustomerRole &&
    currentUser.id.uuid === currentTransaction.customer.id.uuid;

  if (isDataAvailable && isProviderRole && !isOwnSale) {
    // eslint-disable-next-line no-console
    console.error('Tried to access a sale that was not owned by the current user');
    return <NamedRedirect name="InboxPage" params={{ tab: 'sales' }} />;
  } else if (isDataAvailable && isCustomerRole && !isOwnOrder) {
    // eslint-disable-next-line no-console
    console.error('Tried to access an order that was not owned by the current user');
    return <NamedRedirect name="InboxPage" params={{ tab: 'orders' }} />;
  }

  const schemaTitle = intl.formatMessage({
    id: 'ApplicationPage.schemaTitle',
  });

  const handleAcceptApplication = values => {
    onShowProfileListing(currentTransaction.customer.id).then(profileListing => {
      const protectedData = currentTransaction.attributes.protectedData;
      const applicationId = currentTransaction.id.uuid;
      const listingId = currentListing.id.uuid;

      protectedData.applicationId = applicationId;
      protectedData.listingId = listingId;

      const isLightEntrepreneur =
        currentTransaction.customer.attributes.profile.publicData.account_role ===
        'light-entrepreneur';

      onCreateReverseTransaction(
        profileListing.id.uuid,
        protectedData,
        isLightEntrepreneur,
        values.message,
        readyAttachmentFileIds(stagedAttachments)
      )
        .then(transactionId => {
          onClearAttachments();
          onAcceptApplication(applicationId, transactionId, values.afterSkip).then(res => {
            const routes = routeConfiguration();

            history.push(
              createResourceLocatorString(
                'OrderDetailsPage',
                routes,
                { id: transactionId.uuid },
                {}
              )
            );
          });
        })
        .catch(() => {
          // Ignore, error handling in duck file
        });
    });
  };

  const handleAcceptApplicationAfterSkip = values => {
    onShowProfileListing(currentTransaction.customer.id).then(profileListing => {
      const protectedData = currentTransaction.attributes.protectedData;

      const applicationId = currentTransaction.id.uuid;
      const listingId = currentListing.id.uuid;

      protectedData.applicationId = applicationId;
      protectedData.listingId = listingId;

      const isLightEntrepreneur =
        currentTransaction.customer.attributes.profile.publicData.account_role ===
        'light-entrepreneur';

      onCreateReverseTransaction(
        profileListing.id.uuid,
        protectedData,
        isLightEntrepreneur,
        values.message,
        readyAttachmentFileIds(stagedAttachments)
      )
        .then(transactionId => {
          onClearAttachments();
          onAcceptAfterSkip(applicationId, transactionId).then(res => {
            const routes = routeConfiguration();

            history.push(
              createResourceLocatorString(
                'OrderDetailsPage',
                routes,
                { id: transactionId.uuid },
                {}
              )
            );
          });
        })
        .catch(() => {
          // Ignore, error handling in duck file
        });
    });
  };

  const loadingOrFailedFetching = fetchTransactionError ? (
    <p className={css.error}>
      <FormattedMessage id="ApplicationPage.fetchTransactionError" />
    </p>
  ) : (
    <p className={css.loading}>
      <FormattedMessage id="ApplicationPage.loading" />
    </p>
  );

  const panel = isDataAvailable ? (
    <ApplicationPanel
      intl={intl}
      currentUser={currentUser}
      transaction={currentTransaction}
      listing={currentListing}
      transactionRole={transactionRole}
      handleAcceptApplication={handleAcceptApplication}
      onSkipApplication={onSkipApplication}
      handleAcceptApplicationAfterSkip={handleAcceptApplicationAfterSkip}
      sendAcceptApplicationInProgress={sendAcceptApplicationInProgress}
      showProfileListingInProgress={showProfileListingInProgress}
      sendCreateReverseTransactionInProgress={sendCreateReverseTransactionInProgress}
      sendSkipApplicationInProgress={sendSkipApplicationInProgress}
      sendAcceptAfterSkipInProgress={sendAcceptAfterSkipInProgress}
      acceptApplicationError={sendAcceptApplicationError}
      profileListingError={showProfileListingError}
      reverseTransActionError={sendCreateReverseTransactionError}
      skipApplicationError={sendSkipApplicationError}
      acceptAferSkipError={sendAcceptAfterSkipError}
      portfolioWorks={portfolioWorks}
      fetchPortfolioWorksInProgress={fetchPortfolioWorksInProgress}
      onManageDisableScrolling={onManageDisableScrolling}
      skillOptions={skillOptions}
      toolOptions={toolOptions}
      stagedAttachments={stagedAttachments}
      onUploadAttachment={onUploadAttachment}
      onRemoveAttachment={onRemoveAttachment}
    />
  ) : (
    loadingOrFailedFetching
  );

  return (
    <Page title={schemaTitle} scrollingDisabled={isScrollingDisabled}>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="ApplicationPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.root}>{panel}</div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

const { arrayOf, bool, func, oneOf, shape, string } = PropTypes;

ApplicationPageComponent.propTypes = {
  isScrollingDisabled: bool.isRequired,
  transaction: propTypes.transaction,
  currentUser: propTypes.currentUser,
  transactionRole: oneOf([PROVIDER, CUSTOMER]).isRequired,
  params: shape({ id: string }).isRequired,
  fetchTransactionError: propTypes.error,
  onAcceptApplication: func.isRequired,
  onCreateReverseTransaction: func.isRequired,
  onShowProfileListing: func.isRequired,
  onSkipApplication: func.isRequired,
  onAcceptAfterSkip: func.isRequired,
  stagedAttachments: PropTypes.array.isRequired,
  onUploadAttachment: func.isRequired,
  onRemoveAttachment: func.isRequired,
  onClearAttachments: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  sendAcceptApplicationInProgress: bool.isRequired,
  showProfileListingInProgress: bool.isRequired,
  sendCreateReverseTransactionInProgress: bool.isRequired,
  sendSkipApplicationInProgress: bool.isRequired,
  sendAcceptAfterSkipInProgress: bool.isRequired,
  sendAcceptApplicationError: propTypes.error,
  showProfileListingError: propTypes.error,
  sendCreateReverseTransactionError: propTypes.error,
  sendSkipApplicationError: propTypes.error,
  sendAcceptAfterSkipError: propTypes.error,
  portfolioWorks: arrayOf(PropTypes.object),
  fetchPortfolioWorksInProgress: bool,
  skillOptions: arrayOf(PropTypes.object),
  toolOptions: arrayOf(PropTypes.object),
  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const {
    transactionRef,
    fetchTransactionError,
    sendAcceptApplicationInProgress,
    showProfileListingInProgress,
    sendCreateReverseTransactionInProgress,
    sendSkipApplicationInProgress,
    sendAcceptAfterSkipInProgress,
    sendAcceptApplicationError,
    showProfileListingError,
    sendCreateReverseTransactionError,
    sendSkipApplicationError,
    sendAcceptAfterSkipError,
    portfolioWorks,
    fetchPortfolioWorksInProgress,
  } = state.ApplicationPage;
  const { currentUser } = state.user;
  const { skillOptions, toolOptions } = state.SkillsAndTools;

  const transactions = getMarketplaceEntities(state, transactionRef ? [transactionRef] : []);

  const transaction = transactions.length > 0 ? transactions[0] : null;

  return {
    isScrollingDisabled: isScrollingDisabled(state),
    transaction,
    currentUser,
    fetchTransactionError,
    sendAcceptApplicationInProgress,
    showProfileListingInProgress,
    sendCreateReverseTransactionInProgress,
    sendSkipApplicationInProgress,
    sendAcceptAfterSkipInProgress,
    sendAcceptApplicationError,
    showProfileListingError,
    sendCreateReverseTransactionError,
    sendSkipApplicationError,
    sendAcceptAfterSkipError,
    portfolioWorks,
    fetchPortfolioWorksInProgress,
    skillOptions,
    toolOptions,
    stagedAttachments: stagedAttachmentsList(state, SCOPE_APPLICATION_PANEL),
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onCreateReverseTransaction: (listingId, data, isLightEntrepreneur, message, fileIds) =>
      dispatch(createReverseTransaction(listingId, data, isLightEntrepreneur, message, fileIds)),
    onUploadAttachment: file => dispatch(uploadAttachment(SCOPE_APPLICATION_PANEL, file)),
    onRemoveAttachment: tempId => dispatch(removeAttachment(SCOPE_APPLICATION_PANEL, tempId)),
    onClearAttachments: () => dispatch(clearStagedAttachments(SCOPE_APPLICATION_PANEL)),
    onShowProfileListing: userId => dispatch(showProfileListing(userId)),
    onAcceptApplication: (id, transactionId, afterSkip) =>
      dispatch(acceptApplication(id, transactionId, afterSkip)),
    onSkipApplication: id => dispatch(skipApplication(id)),
    onAcceptAfterSkip: (id, transactionId) => dispatch(acceptAfterSkip(id, transactionId)),
    onManageDisableScrolling: (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
  };
};

const ApplicationPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(ApplicationPageComponent);

ApplicationPage.loadData = loadData;
ApplicationPage.setInitialValues = setInitialValues;

export default ApplicationPage;
